import { mountMobile, type MobilePane } from "./view/mobile.js";
import { launchVelocity, type SandboxTool } from "./world/interaction.js";
import "./style.css";
import type { Entity } from "./core/world.js";
import type { WorldHandle, WorldSnapshot } from "./view/world-view.js";
import type { AppAction, RenderEffects } from "./app/state.js";
import { applyTrails, initialState, reduce } from "./app/state.js";
import { SCENARIOS, scenarioById } from "./world/scenarios.js";
import { TrailHistory, type TrailPoint } from "./world/trails.js";
import { entityById } from "./core/world.js";
import { centreOfMass, energyDrift } from "./core/nbody.js";
import { valueIn } from "./core/units.js";
import { EXPERIMENTS, FEATURED_EXPERIMENT } from "./experiment/registry.js";
import {
  formatDistance,
  formatDrift,
  formatElapsed,
  formatMass,
  formatRadius,
  formatSpeed,
  renderAnalysisTabs,
  renderEntityList,
  renderHud,
  renderInspector,
  renderScenarioBar,
} from "./view/panels.js";
import { mountWorld, renderWorldStage } from "./view/world-view.js";
import { escape, escapeAttribute } from "./view/format.js";
import { fr } from "./i18n/fr.js";

/**
 * Le point d'entrée — **un monde, et des panneaux posés dessus**.
 *
 * Ce fichier ne connaît aucune physique : il tient un état, le donne à la vue,
 * et rebranche les panneaux quand il change. Ce qu'il a cessé de faire est plus
 * parlant que ce qu'il fait — il ne démonte plus rien. La vue 3D est montée une
 * fois et vit jusqu'à la fermeture de l'onglet ; charger un scénario ne fait
 * que remplacer les corps qu'elle dessine.
 *
 * ## Où vit le monde
 *
 * Ni dans la vue, ni dans le DOM : dans `app/state.ts`, qui tient l'état et
 * toutes ses transitions **sans DOM, sans Three.js et sans horloge**. Ce fichier
 * n'en garde que la variable courante, le montage et les branchements. La
 * séparation n'est pas cosmétique — le réducteur y est testable, alors qu'ici
 * rien ne l'est : importer ce module écrit dans `#app`, monte un contexte WebGL
 * et démarre une boucle d'images.
 *
 * La vue demande au monde d'avancer une fois par image et lui demande ce qu'il
 * faut dessiner ; les panneaux, eux, ne se réécrivent qu'à basse fréquence.
 * Écrire « 3,41 ans » soixante fois par seconde coûterait plus cher que la
 * simulation elle-même, et personne ne lirait la différence.
 */

const AU = 1.496e11;
/** Images entre deux réécritures des panneaux : quatre par seconde suffisent à lire. */
const SYNC_EVERY = 15;

// ── L'état ───────────────────────────────────────────────────────────────────

/**
 * L'état et ses transitions vivent dans `app/state.ts`, sans DOM ni horloge.
 * Ce fichier n'en garde que la variable, le montage et les branchements.
 */
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
let state = initialState();
if (reducedMotion.matches) state = { ...state, running: false };
const trails = new TrailHistory();

function selected(): Entity | undefined {
  return entityById(state.world, state.selectedId);
}

function barycentre(): TrailPoint {
  const centre = centreOfMass(state.world.entities);
  return { x: valueIn(centre.x, "m"), y: valueIn(centre.y, "m") };
}

function distanceToCentre(one: Entity): number {
  const centre = barycentre();
  return Math.hypot(
    valueIn(one.position.x, "m") - centre.x,
    valueIn(one.position.y, "m") - centre.y,
  );
}

function speedOf(one: Entity): number {
  return Math.hypot(valueIn(one.velocity.x, "m/s"), valueIn(one.velocity.y, "m/s"));
}

function driftNow(): number {
  return valueIn(energyDrift(state.origin.entities, state.world.entities), "1");
}

// ── La coquille ──────────────────────────────────────────────────────────────

const host = document.querySelector("#app");
if (host === null) throw new Error("hôte introuvable");
/** Rétréci une fois pour toutes : le contrôle ne franchit pas les fermetures. */
const root: Element = host;

let theme: "light" | "dark" = "dark";
try {
  if (localStorage.getItem("verlet-theme") === "light") theme = "light";
} catch { /* Le thème reste utilisable quand le stockage est indisponible. */ }
document.documentElement.dataset["theme"] = theme;


root.innerHTML = [
  `<div class="verlet-app">`,
  `<header class="verlet-bar">`,
  `<h1 class="verlet-bar__brand">${escape(fr.appTitle)}</h1>`,
  `<span class="verlet-tagline">${escape(fr.sandboxTagline)}</span>`,
  `<div data-scenarios>${renderScenarioBar(SCENARIOS, state.scenarioId)}</div>`,
  `<div class="verlet-bar__end">`,
  `<button type="button" class="verlet-chip" data-theme-toggle>`,
  `${escape(theme === "dark" ? fr.themeLight : fr.themeDark)}</button>`,

  `<button type="button" class="verlet-chip verlet-bar__drawer" data-drawer-open`,
  ` aria-expanded="false" aria-controls="verlet-analysis-drawer">`,
  `${escape(fr.worldAnalysesLabel)}</button>`,
  `</div>`,
  `</header>`,
  `<main class="verlet-main">`,
  renderWorldStage(),
  `<div class="verlet-tools" role="toolbar" aria-label="${escapeAttribute(fr.sandboxTools)}">`,
  ...([ ["select", fr.toolSelect], ["planet", fr.toolPlanet], ["star", fr.toolStar], ["move", fr.toolMove], ["velocity", fr.toolVelocity] ] as const).map(([tool, label]) =>
    `<button type="button" class="verlet-chip" data-tool="${tool}" aria-pressed="${tool === "select"}">${escape(label)}</button>`),
  `<button type="button" class="verlet-chip" data-reframe>${escape(fr.sandboxReframe)}</button>`,
  `<button type="button" class="verlet-chip" data-panels aria-pressed="true">${escape(fr.sandboxPanels)}</button>`,
  `</div>`,
  `<div class="verlet-tool-status"><span data-tool-status role="status">${escape(fr.toolReady)}</span>`,
  `<button type="button" class="verlet-chip" data-tool-confirm hidden>${escape(fr.mobileConfirm)}</button>`,
  `<button type="button" class="verlet-chip" data-tool-cancel hidden>${escape(fr.toolCancel)}</button></div>`,
  `<aside class="verlet-panel verlet-panel--left" data-entities`,
  ` aria-labelledby="verlet-entities-title"></aside>`,
  `<aside class="verlet-panel verlet-panel--right" data-inspector`,
  ` aria-labelledby="verlet-inspector-title"></aside>`,
  `<div class="verlet-hud" data-hud></div>`,
  `<nav class="verlet-mobile-nav" aria-label="${escape(fr.mobileNavigation)}">`,
  `<button type="button" data-mobile-pane="tools" aria-expanded="false"><span aria-hidden="true">＋</span>${escape(fr.mobileAdd)}</button>`,
  `<button type="button" data-mobile-pane="bodies" aria-expanded="false"><span aria-hidden="true">◉</span>${escape(fr.mobileList)} <span data-body-count></span></button>`,
  `<button type="button" data-mobile-pane="inspector" aria-expanded="false"><span aria-hidden="true">☷</span>${escape(fr.mobileEdit)}</button>`,
  `</nav>`,
  `<div class="verlet-mobile-selection"><button type="button" data-mobile-pane="inspector" aria-expanded="false" aria-label="${escape(fr.mobileSelected)}"><span data-selected-label></span><span aria-hidden="true"> ›</span></button>`,
  `<button type="button" data-mobile-reframe aria-label="${escape(fr.sandboxReframe)}">⌖</button></div>`,
  `<p class="verlet-touch-hint">${escape(fr.mobileHint)}</p>`,
  `</main>`,
  `<section class="verlet-drawer" id="verlet-analysis-drawer" data-drawer hidden`,
  ` aria-labelledby="verlet-analysis-title">`,
  `<header class="verlet-drawer__head">`,
  `<div>`,
  `<h2 id="verlet-analysis-title" tabindex="-1">${escape(fr.worldAnalysesLabel)}</h2>`,
  `<p class="verlet-drawer__lede">${escape(fr.worldAnalysesLede)}</p>`,
  `</div>`,
  `<button type="button" class="verlet-chip" data-drawer-close>`,
  escape(fr.worldAnalysesClose),
  `</button>`,
  `</header>`,
  `<details class="verlet-about"><summary>${escape(fr.sandboxAbout)}</summary>`,
  `<p>${escape(fr.sandboxLimits)}</p>`,
  // Le refus du monde et l'endroit où la confrontation existe vraiment sont
  // dans la même note : c'est là qu'on lit « ce monde ne confronte rien », donc
  // c'est là que la suite doit être offerte. Séparés, le refus se lirait comme
  // une impasse.
  `<p class="verlet-world-note" role="note">`,
  `<span data-world-confronts>`,
  `${escape(scenarioById(state.scenarioId)?.confronts ?? fr.worldNoComparison)}</span>`,
  FEATURED_EXPERIMENT === undefined
    ? ""
    : [
        `<span class="verlet-world-note__bridge">${escape(fr.worldFeaturedBridge)}</span>`,
        `<button type="button" class="verlet-chip verlet-featured verlet-world-note__link"`,
        ` data-featured="${escapeAttribute(FEATURED_EXPERIMENT.id)}">`,
        `${escape(FEATURED_EXPERIMENT.question)}</button>`,
      ].join(""),
  `</p>`,
  `</details>`,
  `<div class="verlet-chips" role="tablist"`,
  ` aria-label="${escapeAttribute(fr.worldAnalysesLabel)}" data-analyses></div>`,
  `<div class="verlet-analysis" id="verlet-analysis-panel"`,
  ` role="tabpanel" data-analysis-host></div>`,
  `</section>`,
  `</div>`,
].join("");

/**
 * La coquille vient d'être écrite juste au-dessus : une région manquante est un
 * défaut de programmation, pas un cas à gérer. On échoue bruyamment plutôt que
 * de laisser un panneau muet.
 */
function region(selector: string): HTMLElement {
  const found = root.querySelector<HTMLElement>(selector);
  if (found === null) throw new Error(`région absente de la coquille : ${selector}`);
  return found;
}

const entitiesPanel = region("[data-entities]");
const themeToggle = region("[data-theme-toggle]");
themeToggle.addEventListener("click", () => {
  theme = theme === "dark" ? "light" : "dark";
  document.documentElement.dataset["theme"] = theme;
  themeToggle.textContent = theme === "dark" ? fr.themeLight : fr.themeDark;
  try { localStorage.setItem("verlet-theme", theme); } catch { /* Choix conservé pour cette page. */ }
  document.dispatchEvent(new Event("verlet-theme-change"));
});

const inspectorPanel = region("[data-inspector]");
const hudPanel = region("[data-hud]");
const worldConfronts = region("[data-world-confronts]");
const scenarioBar = region("[data-scenarios]");
const drawer = region("[data-drawer]");
const analysesNav = region("[data-analyses]");
const analysisHost = region("[data-analysis-host]");

const focusKeys = [
  "scenario",
  "entity",
  "add",
  "reset",
  "follow",
  "remove",
  "play",
] as const;

interface FocusIdentity {
  readonly key: (typeof focusKeys)[number];
  readonly value: string;
}

function focusIdentity(host: HTMLElement): FocusIdentity | undefined {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !host.contains(active)) return;
  for (const key of focusKeys) {
    if (active.hasAttribute(`data-${key}`)) {
      return { key, value: active.dataset[key] ?? "" };
    }
  }
}

function restoreFocus(host: HTMLElement, identity: FocusIdentity | undefined): void {
  if (identity === undefined) return;
  for (const candidate of host.querySelectorAll<HTMLElement>(`[data-${identity.key}]`)) {
    if ((candidate.dataset[identity.key] ?? "") !== identity.value) continue;
    candidate.focus();
    return;
  }
}

function replaceRegion(host: HTMLElement, html: string): void {
  const identity = focusIdentity(host);
  host.innerHTML = html;
  restoreFocus(host, identity);
}

// ── Le rendu des panneaux ────────────────────────────────────────────────────

function refreshEntities(): void {
  root.querySelector("[data-body-count]")?.replaceChildren(String(state.world.entities.length));
  replaceRegion(
    entitiesPanel,
    renderEntityList(
      state.world.entities.map((one) => ({ id: one.id, label: one.label, kind: one.kind })),
      state.selectedId,
    ),
  );
}

function refreshInspector(): void {
  const one = selected();
  root.querySelector("[data-selected-label]")?.replaceChildren(one?.label ?? fr.worldNoSelection);
  root.querySelector(".verlet-mobile-selection [data-mobile-pane]")?.setAttribute("aria-label", `${fr.mobileSelected} : ${one?.label ?? fr.worldNoSelection}`);
  replaceRegion(
    inspectorPanel,
    renderInspector(
      one === undefined
        ? undefined
        : {
            id: one.id,
            label: one.label,
            kind: one.kind,
            mass: valueIn(one.mass, "kg"),
            distance: distanceToCentre(one),
            speed: speedOf(one),
            radius: valueIn(one.radius, "m"),
            following: state.focusId === one.id,
            vx: valueIn(one.velocity.x, "m/s"),
            vy: valueIn(one.velocity.y, "m/s"),
          },
    ),
  );
}

/**
 * Met à jour les chiffres de l'inspecteur **sans le réécrire**.
 *
 * Réécrire le panneau arracherait le curseur de masse en cours de glissement :
 * l'élément disparaîtrait sous le doigt et le geste s'interromprait à chaque
 * synchronisation.
 */
function refreshInspectorFacts(): void {
  const one = selected();
  if (one === undefined) return;
  const fact = (name: string): Element | null => inspectorPanel.querySelector(`[data-fact="${name}"]`);
  const mass = formatMass(valueIn(one.mass, "kg"));
  fact("mass")?.replaceChildren(mass);
  fact("distance")?.replaceChildren(formatDistance(distanceToCentre(one)));
  fact("speed")?.replaceChildren(formatSpeed(speedOf(one)));
  fact("radius")?.replaceChildren(formatRadius(valueIn(one.radius, "m")));
  inspectorPanel.querySelector("[data-mass]")?.setAttribute("aria-valuetext", mass);
  const motion = inspectorPanel.querySelector<HTMLFormElement>("[data-motion]");
  if (motion !== null && !motion.contains(document.activeElement)) {
    const vx = valueIn(one.velocity.x, "m/s");
    const vy = valueIn(one.velocity.y, "m/s");
    (motion.elements.namedItem("speed") as HTMLInputElement).value = (Math.hypot(vx, vy) / 1000).toFixed(2);
    (motion.elements.namedItem("angle") as HTMLInputElement).value = (Math.atan2(vy, vx) * 180 / Math.PI).toFixed(2);
  }
}

function refreshHud(): void {
  replaceRegion(
    hudPanel,
    renderHud({
      running: state.running,
      stepsPerFrame: state.stepsPerFrame,
      elapsed: valueIn(state.world.elapsed, "s"),
      drift: driftNow(),
    }),
  );
}

function refreshHudReadouts(): void {
  hudPanel
    .querySelector("[data-elapsed]")
    ?.replaceChildren(formatElapsed(valueIn(state.world.elapsed, "s")));
  hudPanel.querySelector("[data-drift]")?.replaceChildren(formatDrift(driftNow()));
}

// ── Le monde qui avance ──────────────────────────────────────────────────────

/**
 * L'instantané que reçoit la vue.
 *
 * Elle ne reçoit **pas** un repère : elle en choisit un, parce qu'elle seule
 * tient la caméra. Ce module se contente de dire quels corps existent, lequel
 * est sélectionné, et auquel la caméra est accrochée.
 */
function snapshot(): WorldSnapshot {
  return {
    selectedId: state.selectedId,
    focusId: state.focusId,
    systemScale: AU,
    entities: state.world.entities.map((one) => ({
      id: one.id,
      label: one.label,
      kind: one.kind,
      radius: valueIn(one.radius, "m"),
      position: { x: valueIn(one.position.x, "m"), y: valueIn(one.position.y, "m") },
      trail: trails.pointsFor(one.id),
    })),
  };
}

let sinceSync = 0;

function renderEffects(effects: RenderEffects): void {
  if (effects.scenarioBar) {
    replaceRegion(scenarioBar, renderScenarioBar(SCENARIOS, state.scenarioId));
    worldConfronts.textContent = scenarioById(state.scenarioId)?.confronts ?? fr.worldNoComparison;
  }
  if (effects.entities) refreshEntities();
  if (effects.inspector === "full") refreshInspector();
  if (effects.inspector === "facts") refreshInspectorFacts();
  if (effects.hud) refreshHud();
  if (effects.reframe) view?.reframe();
  if (effects.redraw) view?.redraw();
  if (effects.lookAt !== undefined) view?.lookAt(effects.lookAt);
}

/**
 * Applique une transition : le nouvel état, puis ce qu'elle demande de
 * redessiner et de faire aux sillages.
 *
 * L'ordre compte. Les sillages sont mis à jour avant le rendu, sans quoi une
 * image serait dessinée avec l'historique de l'état précédent.
 */
function dispatch(action: AppAction): void {
  const reduced = reduce(state, action);
  state = reduced.state;
  applyTrails(trails, state.world, reduced.effects);
  renderEffects(reduced.effects);
}

let activeTool: SandboxTool = "select";
const mobile = mountMobile(root);
const view: WorldHandle = mountWorld(root, {
  snapshot,
  tool: () => activeTool,
  confirmBeforeCommit: () => mobile?.compact() ?? false,
  draftChanged: (ready) => { region("[data-tool-confirm]").hidden = !ready; },
  suspended: () => (mobile?.isOpen() ?? false) || !drawer.hidden,
  gesture: (start, end) => {
    const velocity = launchVelocity(start, end);
    if (activeTool === "planet" || activeTool === "star") {
      dispatch({ type: "place-body", kind: activeTool, at: { x: start.x, y: start.y, vx: velocity.x, vy: velocity.y } });
    } else if (activeTool === "move") {
      dispatch({ type: "move-body", x: end.x, y: end.y });
    } else if (activeTool === "velocity") {
      dispatch({ type: "set-velocity", vx: velocity.x, vy: velocity.y });
    }
    setTool("select");
    region("[data-tool-status]").textContent = fr.toolDone;
  },
  preview: (speed) => {
    region("[data-tool-status]").textContent = fr.toolSpeed((speed / 1000).toFixed(1).replace(".", ","));
  },
  advance: () => dispatch({ type: "advance" }),
  running: () => state.running,
  select: (id) => {
    dispatch({ type: "select", id, travel: false });
  },
  focus: (id) => dispatch({ type: "focus", id }),
  synchronise: () => {
    sinceSync += 1;
    if (sinceSync < SYNC_EVERY) return;
    sinceSync = 0;
    refreshHudReadouts();
    refreshInspectorFacts();
  },
});

function setTool(tool: SandboxTool): void {
  if ((tool === "move" || tool === "velocity") && selected() === undefined) {
    region("[data-tool-status]").textContent = fr.toolNeedSelection;
    mobile?.report(fr.toolNeedSelection);
    return;
  }
  mobile?.close();
  activeTool = tool;
  region(".verlet-app").classList.toggle("is-editing", tool !== "select");
  region("[data-tool-confirm]").hidden = true;
  if (tool !== "select") region("[data-world-stage]").focus({ preventScroll: true });
  if (tool !== "select" && state.running) dispatch({ type: "toggle-running" });
  region("[data-tool-status]").textContent = tool === "select" ? fr.toolReady
    : tool === "move" ? fr.toolMoveHint : tool === "velocity" ? fr.toolLaunchHint : fr.toolPlaceHint;
  if (mobile?.compact() && tool !== "select") {
    region("[data-tool-status]").textContent = tool === "move" ? fr.mobileMove : tool === "velocity" ? fr.mobileLaunch : fr.mobilePlace;
  }
  region("[data-tool-cancel]").textContent = mobile?.compact() ? fr.mobileCancel : fr.toolCancel;
  region("[data-tool-cancel]").hidden = tool === "select";
  for (const button of root.querySelectorAll<HTMLElement>("[data-tool]")) {
    button.setAttribute("aria-pressed", String(button.dataset["tool"] === tool));
  }
  view?.redraw();
}
region(".verlet-tools").addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const button = target.closest<HTMLElement>("[data-tool]");
  if (button !== null) {
    const tool = button.dataset["tool"] as SandboxTool;
    void view.ready.then((ready) => {
      if (ready || tool === "select") setTool(tool === activeTool ? "select" : tool);
      else {
        region("[data-tool-status]").textContent = fr.toolUnavailable;
        mobile?.report(fr.toolUnavailable);
        region(".verlet-touch-hint").textContent = fr.toolUnavailable;
      }
    });
  }
  if (target.closest("[data-reframe]") !== null) {
    dispatch({ type: "focus", id: "" });
    view.reframe();
  }
  if (target.closest("[data-panels]") !== null) {
    const hidden = region(".verlet-app").classList.toggle("panels-hidden");
    region("[data-panels]").setAttribute("aria-pressed", String(!hidden));
  }
});
region("[data-tool-confirm]").addEventListener("click", () => view.confirm());
region("[data-tool-cancel]").addEventListener("click", () => setTool("select"));
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setTool("select");
});
inspectorPanel.addEventListener("submit", (event) => {
  event.preventDefault();
  const form = (event.target as HTMLFormElement);
  if (!form.matches("[data-motion]") || !form.reportValidity()) return;
  const speed = Number((form.elements.namedItem("speed") as HTMLInputElement).value) * 1000;
  const angle = Number((form.elements.namedItem("angle") as HTMLInputElement).value) * Math.PI / 180;
  if (state.running) dispatch({ type: "toggle-running" });
  dispatch({ type: "set-velocity", vx: speed * Math.cos(angle), vy: speed * Math.sin(angle) });
});

// ── Les branchements ─────────────────────────────────────────────────────────

scenarioBar.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>("[data-scenario]");
  if (button === null) return;
  setTool("select");
  dispatch({ type: "load-scenario", id: button.dataset["scenario"] ?? "" });
});

entitiesPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;

  const entity = target.closest<HTMLElement>("[data-entity]");
  if (entity !== null) {
    dispatch({ type: "select", id: entity.dataset["entity"] ?? "", travel: false });
    mobile?.open("inspector");
    return;
  }

  if (target.closest("[data-add]") !== null) {
    setTool("select");
    dispatch({ type: "add-planet" });
    return;
  }

  if (target.closest("[data-reset]") !== null) {
    setTool("select");
    dispatch({ type: "load-scenario", id: state.scenarioId });
  }
});

inspectorPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;

  if (target.closest("[data-stop]") !== null) {
    if (state.running) dispatch({ type: "toggle-running" });
    dispatch({ type: "set-velocity", vx: 0, vy: 0 });
    return;
  }

  if (target.closest("[data-follow]") !== null) {
    dispatch({ type: "toggle-follow" });
    return;
  }

  if (target.closest("[data-remove]") !== null) {
    setTool("select");
    dispatch({ type: "remove-selected" });
    entitiesPanel.querySelector<HTMLElement>('[data-entity][aria-pressed="true"]')?.focus();
  }
});

inspectorPanel.addEventListener("input", (event) => {
  const slider = (event.target as HTMLElement).closest<HTMLInputElement>("[data-mass]");
  if (slider === null) return;
  dispatch({ type: "set-mass", kilograms: Math.pow(10, Number(slider.value)) });
});

hudPanel.addEventListener("click", (event) => {
  if ((event.target as HTMLElement).closest("[data-play]") === null) return;
  setTool("select");
  dispatch({ type: "toggle-running" });
});

hudPanel.addEventListener("input", (event) => {
  const slider = (event.target as HTMLElement).closest<HTMLInputElement>("[data-speed]");
  if (slider === null) return;
  const stepsPerFrame = Number(slider.value);
  const value = fr.worldSpeedValue(stepsPerFrame);
  slider.setAttribute("aria-valuetext", value);
  slider.closest("label")?.querySelector("[data-speed-readout]")?.replaceChildren(value);
  slider.closest("label")?.querySelector("[data-mobile-speed]")?.replaceChildren(fr.mobileSpeedValue(stepsPerFrame));
  dispatch({ type: "set-speed", stepsPerFrame });
});

reducedMotion.addEventListener("change", (event) => {
  if (event.matches && state.running) dispatch({ type: "toggle-running" });
});

// ── Le tiroir des analyses ───────────────────────────────────────────────────

const drawerOpen = root.querySelector<HTMLElement>("[data-drawer-open]");
let detachAnalysis: (() => void) | undefined;
/** L'analyse montée, s'il y en a une : on rouvre le tiroir là où on l'a laissé. */
let shownAnalysis: string | undefined;

analysesNav.innerHTML = renderAnalysisTabs(EXPERIMENTS);

function showAnalysis(id: string): void {
  const entry = EXPERIMENTS.find((one) => one.id === id);
  if (entry === undefined) return;
  // Remonter celle qui est déjà là la redessinerait pour rien, et rendrait
  // ses réglages au sommet de la page sous le doigt de qui vient de cliquer
  // l'onglet déjà actif.
  if (id !== shownAnalysis) {
    detachAnalysis?.();
    detachAnalysis = entry.mount(analysisHost);
    shownAnalysis = id;
  }
  for (const button of analysesNav.querySelectorAll<HTMLElement>("[data-analysis]")) {
    const selected = button.dataset["analysis"] === id;
    button.setAttribute("aria-selected", selected ? "true" : "false");
    button.setAttribute("tabindex", selected ? "0" : "-1");
    if (selected) analysisHost.setAttribute("aria-labelledby", button.id);
  }
}

analysesNav.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>("[data-analysis]");
  if (button === null) return;
  showAnalysis(button.dataset["analysis"] ?? "");
});

analysesNav.addEventListener("keydown", (event) => {
  if (!(event instanceof KeyboardEvent)) return;
  if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
  const tabs = [...analysesNav.querySelectorAll<HTMLElement>("[data-analysis]")];
  const current = tabs.findIndex((tab) => tab === document.activeElement);
  if (current < 0 || tabs.length === 0) return;
  event.preventDefault();
  const nextIndex =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
  const next = tabs[nextIndex];
  if (next === undefined) return;
  next.focus();
  showAnalysis(next.dataset["analysis"] ?? "");
});

/**
 * Ouvre le tiroir **sur une analyse nommée**.
 *
 * C'est ce que la pastille « Analyses » ne permettait pas : elle ouvrait sur la
 * première venue, et il fallait savoir qu'une des trois confronte des mesures
 * pour aller la chercher. Les deux entrées mises en avant — celle de la barre
 * et celle de la note du monde — passent par ici avec l'identifiant de
 * l'analyse qu'elles annoncent, si bien qu'un libellé ne peut pas promettre une
 * page et en ouvrir une autre.
 */
function openDrawer(id: string): void {
  mobile?.close();
  drawer.hidden = false;
  if (mobile?.compact()) {
    region(".verlet-main").inert = true;
    region(".verlet-bar").inert = true;
    drawer.setAttribute("role", "dialog");
    drawer.setAttribute("aria-modal", "true");
  }
  drawerOpen?.setAttribute("aria-expanded", "true");
  showAnalysis(id);
  region("#verlet-analysis-title").focus({ preventScroll: true });
  if (!mobile?.compact()) drawer.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
}

drawerOpen?.addEventListener("click", () => {
  openDrawer(shownAnalysis ?? EXPERIMENTS[0]?.id ?? "");
});

for (const entry of root.querySelectorAll<HTMLElement>("[data-featured]")) {
  entry.addEventListener("click", () => {
    openDrawer(entry.dataset["featured"] ?? "");
  });
}

root.querySelector("[data-drawer-close]")?.addEventListener("click", () => {
  drawer.hidden = true;
  region(".verlet-main").inert = false;
  region(".verlet-bar").inert = false;
  drawer.removeAttribute("aria-modal");
  drawer.removeAttribute("role");
  drawerOpen?.setAttribute("aria-expanded", "false");
  window.scrollTo({ top: 0 });
  drawerOpen?.focus({ preventScroll: true });
});

// ── Départ ───────────────────────────────────────────────────────────────────

refreshEntities();
refreshInspector();
refreshHud();


for (const button of root.querySelectorAll<HTMLElement>("[data-mobile-pane]")) {
  button.addEventListener("click", () => mobile?.open(button.dataset["mobilePane"] as MobilePane));
}
region("[data-mobile-reframe]").addEventListener("click", () => {
  dispatch({ type: "focus", id: "" });
  view.reframe();
});
drawer.addEventListener("keydown", (event) => {
  if (!mobile?.compact()) return;
  if (event.key === "Escape") {
    event.preventDefault();
    root.querySelector<HTMLElement>("[data-drawer-close]")?.click();
  }
  if (event.key !== "Tab") return;
  const focusable = [...drawer.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex="0"], a[href]')].filter((node) => node.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (event.shiftKey && (document.activeElement === first || document.activeElement === region("#verlet-analysis-title"))) {
    event.preventDefault(); last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
});
